import React from 'react';
import clsx from 'clsx';
import styles from './index.module.scss';

/**
 * Small red indicator surfaced next to family avatars / menu items to
 * flag "this child has unread alerts". Two visual variants:
 *
 * - `bare` (default) — plain filled circle. Used on the sidebar
 *   "My Family" item where the dot sits next to the icon and doesn't
 *   need to grab any more attention than an app-icon badge.
 * - `withExclamation` — 15px red circle with a white "!" inside, per
 *   Figma 1:6404 / 46:12185 on plan-members rows and the protection-
 *   details header. Use the exported `.avatarCorner` class for placement.
 */

interface UnreadAlertsDotProps {
  variant?: 'bare' | 'withExclamation';
  size?: number;
  className?: string;
  qaSelector?: string;
}

function UnreadAlertsDot({
  variant = 'bare',
  size,
  className,
  qaSelector,
}: UnreadAlertsDotProps) {
  const style = size
    ? ({ '--dot-size': `${size}px` } as React.CSSProperties)
    : undefined;
  return (
    <span
      role='status'
      aria-label='Unread alerts'
      className={clsx(
        styles.dot,
        variant === 'withExclamation' && styles.withExclamation,
        className,
      )}
      style={style}
      data-qa-selector={qaSelector}
    >
      {variant === 'withExclamation' && (
        <span aria-hidden='true' className={styles.exclamation}>
          !
        </span>
      )}
    </span>
  );
}

export default UnreadAlertsDot;
